/**
 * AnimatedAccordion custom element
 * Handles smooth open/close animations for <details> elements
 * Required because browsers remove [open] attribute immediately on close,
 * preventing CSS transitions from running.
 */

// Only define if not already registered
if (!customElements.get('animated-accordion')) {

class AnimatedAccordion extends HTMLElement {
  constructor() {
    super();
    this.details = null;
    this.summary = null;
    this.contentWrapper = null;
    this.isAnimating = false;
    this.initialized = false;
    
    // Bind handlers once so they can be removed later
    this.boundHandleClick = this.handleClick.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    // Find the details element
    this.details = this.querySelector('details');
    if (!this.details) return;

    this.summary = this.details.querySelector('summary');
    this.contentWrapper = this.details.querySelector('.accordion__content-wrapper');

    if (!this.summary || !this.contentWrapper) return;

    // Prevent default toggle and handle manually
    this.summary.addEventListener('click', this.boundHandleClick);
    this.summary.addEventListener('keydown', this.boundHandleKeydown);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.summary) {
      this.summary.removeEventListener('click', this.boundHandleClick);
      this.summary.removeEventListener('keydown', this.boundHandleKeydown);
    }
  }

  handleClick(e) {
    e.preventDefault();
    this.toggle();
  }

  handleKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggle();
    }
  }

  toggle() {
    if (this.isAnimating) return;

    if (this.details.open) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.isAnimating = true;

    // For first animation after element becomes visible, we need to ensure
    // the browser has computed the initial (closed) state before transitioning.
    // Using double-RAF ensures the initial state is painted.
    if (!this.initialized) {
      this.initialized = true;
      // Force style computation
      getComputedStyle(this.contentWrapper).gridTemplateRows;
      // Use RAF to defer the actual open to next frame
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.performOpen();
        });
      });
    } else {
      this.performOpen();
    }
  }

  performOpen() {
    // Set open attribute to show content
    this.details.open = true;

    // Force reflow to ensure transition runs
    void this.contentWrapper.offsetHeight;

    // Listen for transition end
    const onTransitionEnd = (e) => {
      if (e.target === this.contentWrapper) {
        this.contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        this.isAnimating = false;
      }
    };

    this.contentWrapper.addEventListener('transitionend', onTransitionEnd);

    // Fallback timeout in case transition doesn't fire
    setTimeout(() => {
      this.isAnimating = false;
    }, 400);
  }

  close() {
    this.isAnimating = true;

    // Add closing class to trigger reverse animation
    this.details.classList.add('closing');

    const onTransitionEnd = (e) => {
      if (e.target === this.contentWrapper) {
        this.contentWrapper.removeEventListener('transitionend', onTransitionEnd);
        this.details.open = false;
        this.details.classList.remove('closing');
        this.isAnimating = false;
      }
    };

    this.contentWrapper.addEventListener('transitionend', onTransitionEnd);

    // Fallback timeout in case transition doesn't fire
    setTimeout(() => {
      if (this.details.classList.contains('closing')) {
        this.details.open = false;
        this.details.classList.remove('closing');
        this.isAnimating = false;
      }
    }, 400);
  }
}

// Define the custom element
customElements.define('animated-accordion', AnimatedAccordion);

} // End guard
